// siteThemes.js — Phase 113 Pro Coach Website Builder themes.
// -----------------------------------------------------------------------
// Five premium, hand-tuned themes per /app/design_guidelines.md. Each theme
// is a bundle of CSS custom properties (applied inline on the .csite scope
// element so tokens never leak into the SaaS portal), a font pairing, a
// hero layout style, and button-radius character.
//
// Used by BOTH the public renderer (components/coachsite/*) and the editor
// (LandingPageVisualEditor.jsx theme picker) so they stay in sync.
//
// Legacy mapping (backward compatible — existing coaches keep a close look):
//   ocean  → harbor-calm
//   sunset → sunlit-deck
//   forest → cypress-retreat
//   custom → studio-neutral

export const SITE_THEMES = {
    'harbor-calm': {
        id: 'harbor-calm',
        name: 'Harbor Calm',
        tagline: 'Coastal premium — bright, airy, trust-first.',
        bestFor: 'Family swim coaches · conversion-first',
        heroLayout: 'split', // left copy + right photo card
        fonts: {
            display: "'Playfair Display', Georgia, serif",
            body: "'Figtree', system-ui, sans-serif",
            displayName: 'Playfair Display',
            bodyName: 'Figtree',
        },
        vars: {
            '--t-bg': '#F6FAFC',
            '--t-surface': '#FFFFFF',
            '--t-text': '#0B1B2B',
            '--t-muted': '#4B647A',
            '--t-border': 'rgba(11,27,43,0.12)',
            '--t-primary': '#0E7490',
            '--t-primary-hover': '#0B5F76',
            '--t-on-primary': '#FFFFFF',
            '--t-accent': '#F2C14E',
            '--t-on-accent': '#0B1B2B',
            '--t-accent-2': '#7DD3FC',
            '--t-radius-card': '1.25rem',
            '--t-radius-btn': '0.75rem',
            '--t-font-display': "'Playfair Display', Georgia, serif",
            '--t-font-body': "'Figtree', system-ui, sans-serif",
            '--t-hero-wash': 'linear-gradient(120deg, rgba(14,116,144,0.10) 0%, rgba(125,211,252,0.14) 100%)',
            '--t-dark-band': '#0B1B2B',
        },
        swatch: ['#0E7490', '#F2C14E', '#F6FAFC'],
    },
    'sunlit-deck': {
        id: 'sunlit-deck',
        name: 'Sunlit Deck',
        tagline: 'Warm, optimistic, boutique — a premium summer club.',
        bestFor: 'Instagram-first coaches · lifestyle brands',
        heroLayout: 'photo-card', // full-bleed photo + bottom-left content card
        fonts: {
            display: "'Crimson Text', Georgia, serif",
            body: "'Montserrat', system-ui, sans-serif",
            displayName: 'Crimson Text',
            bodyName: 'Montserrat',
        },
        vars: {
            '--t-bg': '#FFF8F0',
            '--t-surface': '#FFFFFF',
            '--t-text': '#1F2937',
            '--t-muted': '#6B7280',
            '--t-border': 'rgba(31,41,55,0.12)',
            '--t-primary': '#EA580C',
            '--t-primary-hover': '#C2410C',
            '--t-on-primary': '#FFFFFF',
            '--t-accent': '#0EA5A4',
            '--t-on-accent': '#FFFFFF',
            '--t-accent-2': '#FDE68A',
            '--t-radius-card': '1.25rem',
            '--t-radius-btn': '1rem',
            '--t-font-display': "'Crimson Text', Georgia, serif",
            '--t-font-body': "'Montserrat', system-ui, sans-serif",
            '--t-hero-wash': 'linear-gradient(120deg, rgba(234,88,12,0.08) 0%, rgba(253,230,138,0.18) 100%)',
            '--t-dark-band': '#3B2410',
        },
        swatch: ['#EA580C', '#0EA5A4', '#FFF8F0'],
    },
    'cypress-retreat': {
        id: 'cypress-retreat',
        name: 'Cypress Retreat',
        tagline: 'Calm, grounded, nature-adjacent — premium, not flashy.',
        bestFor: 'Safety-first · therapy & special-needs swim',
        heroLayout: 'centered', // centered copy + photo strip below
        fonts: {
            display: "'Crimson Text', Georgia, serif",
            body: "'Figtree', system-ui, sans-serif",
            displayName: 'Crimson Text',
            bodyName: 'Figtree',
        },
        vars: {
            '--t-bg': '#F5F7F2',
            '--t-surface': '#FFFFFF',
            '--t-text': '#0F172A',
            '--t-muted': '#475569',
            '--t-border': 'rgba(15,23,42,0.12)',
            '--t-primary': '#166534',
            '--t-primary-hover': '#14532D',
            '--t-on-primary': '#FFFFFF',
            '--t-accent': '#0EA5A4',
            '--t-on-accent': '#FFFFFF',
            '--t-accent-2': '#E7E5E4',
            '--t-radius-card': '1rem',
            '--t-radius-btn': '0.625rem',
            '--t-font-display': "'Crimson Text', Georgia, serif",
            '--t-font-body': "'Figtree', system-ui, sans-serif",
            '--t-hero-wash': 'linear-gradient(120deg, rgba(22,101,52,0.08) 0%, rgba(14,165,164,0.10) 100%)',
            '--t-dark-band': '#122A1C',
        },
        swatch: ['#166534', '#0EA5A4', '#F5F7F2'],
    },
    'lane-lines': {
        id: 'lane-lines',
        name: 'Lane Lines',
        tagline: 'Modern athletic + data-driven energy.',
        bestFor: 'Competitive swim · triathlon · adult technique',
        heroLayout: 'bento', // headline + CTAs + metric cards + photo tile
        fonts: {
            display: "'Montserrat', system-ui, sans-serif",
            body: "'Figtree', system-ui, sans-serif",
            displayName: 'Montserrat',
            bodyName: 'Figtree',
            mono: "'Roboto Mono', monospace",
        },
        vars: {
            '--t-bg': '#F7FAFF',
            '--t-surface': '#FFFFFF',
            '--t-text': '#0B1220',
            '--t-muted': '#475569',
            '--t-border': 'rgba(11,18,32,0.12)',
            '--t-primary': '#2563EB',
            '--t-primary-hover': '#1D4ED8',
            '--t-on-primary': '#FFFFFF',
            '--t-accent': '#06B6D4',
            '--t-on-accent': '#062A33',
            '--t-accent-2': '#E2E8F0',
            '--t-radius-card': '1rem',
            '--t-radius-btn': '0.75rem',
            '--t-font-display': "'Montserrat', system-ui, sans-serif",
            '--t-font-body': "'Figtree', system-ui, sans-serif",
            '--t-hero-wash': 'linear-gradient(120deg, rgba(37,99,235,0.10) 0%, rgba(6,182,212,0.12) 100%)',
            '--t-dark-band': '#0B1220',
        },
        swatch: ['#2563EB', '#06B6D4', '#F7FAFF'],
    },
    'studio-neutral': {
        id: 'studio-neutral',
        name: 'Studio Neutral',
        tagline: 'Minimal editorial — photos and copy do the work.',
        bestFor: 'Strong photography · premium minimal brands',
        heroLayout: 'classic', // centered hero + large photo below
        fonts: {
            display: "'Playfair Display', Georgia, serif",
            body: "'DM Sans', system-ui, sans-serif",
            displayName: 'Playfair Display',
            bodyName: 'DM Sans',
        },
        vars: {
            '--t-bg': '#FAFAFA',
            '--t-surface': '#FFFFFF',
            '--t-text': '#111827',
            '--t-muted': '#6B7280',
            '--t-border': 'rgba(17,24,39,0.12)',
            '--t-primary': '#111827',
            '--t-primary-hover': '#0B1220',
            '--t-on-primary': '#FFFFFF',
            '--t-accent': '#0EA5A4',
            '--t-on-accent': '#FFFFFF',
            '--t-accent-2': '#F3F4F6',
            '--t-radius-card': '0.875rem',
            '--t-radius-btn': '0.625rem',
            '--t-font-display': "'Playfair Display', Georgia, serif",
            '--t-font-body': "'DM Sans', system-ui, sans-serif",
            '--t-hero-wash': 'linear-gradient(120deg, rgba(17,24,39,0.04) 0%, rgba(14,165,164,0.06) 100%)',
            '--t-dark-band': '#111827',
        },
        swatch: ['#111827', '#0EA5A4', '#FAFAFA'],
    },
};

export const SITE_THEME_LIST = Object.values(SITE_THEMES);

const LEGACY_MAP = {
    ocean: 'harbor-calm',
    sunset: 'sunlit-deck',
    forest: 'cypress-retreat',
    custom: 'studio-neutral',
};

/** Map a legacy landing theme id (ocean/sunset/forest/custom) → new site theme id. */
export function mapLegacyTheme(legacyId) {
    return LEGACY_MAP[legacyId] || 'harbor-calm';
}

/**
 * Resolve the active site theme for a landing-page payload.
 * Prefers the explicit `site_theme`; falls back to mapping the legacy
 * `theme` so existing Pro coaches keep a familiar palette automatically.
 */
export function getSiteTheme(lp) {
    const explicit = lp?.site_theme;
    if (explicit && SITE_THEMES[explicit]) return SITE_THEMES[explicit];
    return SITE_THEMES[mapLegacyTheme(lp?.theme)];
}
